import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    createTeam,
    getTeam,
    updateTeam,
    deleteTeam,
    getTeamStats,
    getUserTeams
} from 'team/apis/teamApi';
import { useAuth } from 'contexts/AuthContext';
import { useError } from 'contexts/ErrorContext';

const TeamContext = createContext();

export const TeamProvider = ({ children }) => {
    const { getCurrentUserId, isAuthenticated, authState } = useAuth();
    const { addError, handleApiCall } = useError();

    // Team state management
    const [teams, setTeams] = useState({});
    const [currentTeamId, setCurrentTeamId] = useState(null);
    const [teamStats, setTeamStats] = useState({});
    const [userTeams, setUserTeams] = useState([]);
    const [teamError, setTeamError] = useState(null);
    const [loading, setLoading] = useState(false);

    // Cache management
    const [lastFetchTime, setLastFetchTime] = useState({});
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    // Enhanced error handling
    const handleTeamError = useCallback((error, operation = 'Team operation') => {
        console.error(`[TeamContext] ${operation} Error:`, error);
        setTeamError(error.message || 'An unexpected error occurred');
        addError(error.message || 'An unexpected error occurred');
    }, [addError]);

    // Clear team error
    const clearTeamError = useCallback(() => {
        setTeamError(null);
    }, []);

    // Check if data is stale
    const isDataStale = useCallback((key) => {
        const lastFetch = lastFetchTime[key];
        if (!lastFetch) return true;
        return Date.now() - lastFetch > CACHE_DURATION;
    }, [lastFetchTime]);

    // Update cache timestamp
    const updateCacheTimestamp = useCallback((key) => {
        setLastFetchTime(prev => ({
            ...prev,
            [key]: Date.now()
        }));
    }, []);

    // Create team
    const createTeamHandler = useCallback(async (teamData) => {
        try {
            setLoading(true);
            clearTeamError();

            const result = await createTeam(teamData);

            if (result.success) {
                const newTeam = result.team;
                setTeams(prev => ({
                    ...prev,
                    [newTeam._id]: newTeam
                }));
                setUserTeams(prev => [...prev, newTeam]);
                setCurrentTeamId(newTeam._id);
                updateCacheTimestamp('teams');
                updateCacheTimestamp('userTeams');

                console.log('[TeamContext] Team created successfully:', newTeam);
                return { success: true, data: newTeam };
            } else {
                throw new Error(result.message || 'Failed to create team');
            }
        } catch (error) {
            handleTeamError(error, 'Creating team');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [clearTeamError, handleTeamError, updateCacheTimestamp]);

    // Get team by ID
    const getTeamHandler = useCallback(async (teamId, forceRefresh = false) => {
        try {
            if (!teamId) {
                throw new Error('Team ID is required');
            }

            // Check cache first
            if (!forceRefresh && teams[teamId] && !isDataStale(`team_${teamId}`)) {
                return { success: true, data: teams[teamId] };
            }

            setLoading(true);
            clearTeamError();

            const result = await getTeam(teamId);

            if (result.success) {
                const team = result.team;
                setTeams(prev => ({
                    ...prev,
                    [teamId]: team
                }));
                updateCacheTimestamp(`team_${teamId}`);

                console.log('[TeamContext] Team fetched successfully:', team);
                return { success: true, data: team };
            } else {
                throw new Error(result.message || 'Failed to fetch team');
            }
        } catch (error) {
            handleTeamError(error, 'Fetching team');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [teams, isDataStale, clearTeamError, handleTeamError, updateCacheTimestamp]);

    // Update team
    const updateTeamHandler = useCallback(async (teamId, updateData) => {
        try {
            if (!teamId) {
                throw new Error('Team ID is required');
            }

            setLoading(true);
            clearTeamError();

            const result = await updateTeam(teamId, updateData);

            if (result.success) {
                const updatedTeam = result.team;
                setTeams(prev => ({
                    ...prev,
                    [teamId]: updatedTeam
                }));
                setUserTeams(prev =>
                    prev.map(team => team._id === teamId ? updatedTeam : team)
                );
                updateCacheTimestamp(`team_${teamId}`);
                updateCacheTimestamp('teams');
                updateCacheTimestamp('userTeams');

                console.log('[TeamContext] Team updated successfully:', updatedTeam);
                return { success: true, data: updatedTeam };
            } else {
                throw new Error(result.message || 'Failed to update team');
            }
        } catch (error) {
            handleTeamError(error, 'Updating team');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [clearTeamError, handleTeamError, updateCacheTimestamp]);

    // Delete team
    const deleteTeamHandler = useCallback(async (teamId) => {
        try {
            if (!teamId) {
                throw new Error('Team ID is required');
            }

            setLoading(true);
            clearTeamError();

            const result = await deleteTeam(teamId);

            if (result.success) {
                setTeams(prev => {
                    const newTeams = { ...prev };
                    delete newTeams[teamId];
                    return newTeams;
                });
                setUserTeams(prev => prev.filter(team => team._id !== teamId));

                if (currentTeamId === teamId) {
                    setCurrentTeamId(null);
                }

                updateCacheTimestamp('teams');
                updateCacheTimestamp('userTeams');

                console.log('[TeamContext] Team deleted successfully');
                return { success: true };
            } else {
                throw new Error(result.message || 'Failed to delete team');
            }
        } catch (error) {
            handleTeamError(error, 'Deleting team');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [currentTeamId, clearTeamError, handleTeamError, updateCacheTimestamp]);

    // Get team stats
    const getTeamStatsHandler = useCallback(async (teamId, forceRefresh = false) => {
        try {
            if (!teamId) {
                throw new Error('Team ID is required');
            }

            // Check cache first
            if (!forceRefresh && teamStats[teamId] && !isDataStale(`stats_${teamId}`)) {
                return { success: true, data: teamStats[teamId] };
            }

            setLoading(true);
            clearTeamError();

            const result = await getTeamStats(teamId);

            if (result.success) {
                const stats = result.stats;
                setTeamStats(prev => ({
                    ...prev,
                    [teamId]: stats
                }));
                updateCacheTimestamp(`stats_${teamId}`);

                console.log('[TeamContext] Team stats fetched successfully:', stats);
                return { success: true, data: stats };
            } else {
                throw new Error(result.message || 'Failed to fetch team stats');
            }
        } catch (error) {
            handleTeamError(error, 'Fetching team stats');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [isDataStale, clearTeamError, handleTeamError, updateCacheTimestamp]); // Removed teamStats from dependencies

    // Get user teams
    const getUserTeamsHandler = useCallback(async (forceRefresh = false) => {
        try {
            // Check cache first
            if (!forceRefresh && userTeams && userTeams.length > 0 && !isDataStale('userTeams')) {
                return { success: true, data: userTeams };
            }

            setLoading(true);
            clearTeamError();

            const result = await getUserTeams();

            if (result.success) {
                const teams = result.teams;
                setUserTeams(teams);

                // Update teams cache
                const teamsMap = {};
                teams.forEach(team => {
                    teamsMap[team._id] = team;
                });
                setTeams(prev => ({ ...prev, ...teamsMap }));

                // Set the first team as current team if no current team is set
                if (teams.length > 0 && !currentTeamId) {
                    setCurrentTeamId(teams[0]._id);
                    console.log('[TeamContext] Auto-setting current team to first team:', teams[0]._id);
                }

                updateCacheTimestamp('userTeams');

                console.log('[TeamContext] User teams fetched successfully:', teams);
                return { success: true, data: teams };
            } else {
                throw new Error(result.message || 'Failed to fetch user teams');
            }
        } catch (error) {
            handleTeamError(error, 'Fetching user teams');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [isDataStale, clearTeamError, handleTeamError, updateCacheTimestamp]); // Removed userTeams from dependencies

    // Set current team
    const setCurrentTeam = useCallback((teamId) => {
        setCurrentTeamId(teamId);
        console.log('[TeamContext] Current team set to:', teamId);
    }, []);

    // Get current team
    const getCurrentTeam = useCallback(() => {
        return currentTeamId ? teams[currentTeamId] : null;
    }, [currentTeamId, teams]);

    // Refresh all data
    const refreshAllData = useCallback(async () => {
        try {
            setLoading(true);
            clearTeamError();

            // Refresh user teams
            await getUserTeamsHandler(true);

            // If we have a current team, refresh its data
            if (currentTeamId) {
                await getTeamHandler(currentTeamId, true);
                await getTeamStatsHandler(currentTeamId, true);
            }

            console.log('[TeamContext] All data refreshed successfully');
        } catch (error) {
            handleTeamError(error, 'Refreshing all data');
        } finally {
            setLoading(false);
        }
    }, [currentTeamId, getUserTeamsHandler, getTeamHandler, getTeamStatsHandler, clearTeamError, handleTeamError]);

    // Auto-fetch user teams on mount - only for team plan users
    useEffect(() => {
        if (isAuthenticated && getCurrentUserId()) {
            // Only fetch teams if user has team plan
            const userPlan = authState?.user?.plan;
            if (userPlan === 'team') {
                getUserTeamsHandler();
            }
        }
    }, [isAuthenticated, getCurrentUserId, authState?.user?.plan]); // Removed getUserTeamsHandler from dependencies

    // Context value
    const value = {
        // State
        teams,
        currentTeamId,
        teamStats,
        userTeams,
        teamError,
        loading,

        // Actions
        createTeam: createTeamHandler,
        getTeam: getTeamHandler,
        updateTeam: updateTeamHandler,
        deleteTeam: deleteTeamHandler,
        getTeamStats: getTeamStatsHandler,
        getUserTeams: getUserTeamsHandler,
        setCurrentTeam,
        getCurrentTeam,
        refreshAllData,

        // Utility functions
        clearTeamError
    };

    return (
        <TeamContext.Provider value={value}>
            {children}
        </TeamContext.Provider>
    );
};

export const useTeam = () => {
    const context = useContext(TeamContext);
    if (!context) {
        throw new Error('useTeam must be used within a TeamProvider');
    }
    return context;
};